"use client";

import { useState } from "react";
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { parseSearch, SEARCH_HELP } from "@/lib/llm-models/cost-filters";

export const focusRing = "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1";

const ALL = "";

/** A filter dropdown in the Models tab's style; the empty value means "All". */
export function FilterListbox({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { key: string; label: string }[];
}) {
  const selected = options.find((o) => o.key === value);
  return (
    <div className="min-w-0">
      <Listbox value={value} onChange={onChange}>
        <span className="mb-1 block text-xs font-semibold text-slate-600">{label}</span>
        <ListboxButton
          aria-label={`${label}: ${selected?.label ?? "All"}`}
          className="relative flex h-10 w-full items-center rounded-lg border border-slate-300 bg-white pl-3 pr-10 text-left text-sm font-medium text-slate-900 shadow-sm transition hover:border-slate-400 focus:outline-none data-[focus]:border-indigo-500 data-[focus]:ring-2 data-[focus]:ring-indigo-500/20 data-[open]:border-indigo-500 data-[open]:ring-2 data-[open]:ring-indigo-500/20"
        >
          <span className={`truncate ${selected ? "" : "text-slate-500"}`}>{selected?.label ?? "All"}</span>
          <ChevronsUpDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom start"
          transition
          className="z-50 w-[var(--button-width)] min-w-[14rem] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-lg shadow-slate-900/10 [--anchor-gap:6px] [--anchor-max-height:20rem] focus:outline-none transition duration-100 ease-out data-[closed]:-translate-y-1 data-[closed]:opacity-0"
        >
          {[{ key: ALL, label: "All" }, ...options].map((o) => (
            <ListboxOption
              key={o.key || "all"}
              value={o.key}
              className="group flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-800 data-[focus]:bg-indigo-50"
            >
              <Check className="h-4 w-4 shrink-0 text-indigo-600 invisible group-data-[selected]:visible" aria-hidden="true" />
              <span className="min-w-0 font-medium group-data-[selected]:text-indigo-700">{o.label}</span>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  );
}

const fieldClass =
  "h-10 w-full min-w-0 rounded-lg border border-slate-300 bg-white text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 hover:border-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20";

const checkCallSearch = (v: string) => (parseSearch(v) ? null : SEARCH_HELP);

/**
 * A search box, checked before it's sent. By default "Find calls": a lesson number, learner email
 * or id, job id or OpenRouter id.
 */
export function SearchBox({
  value,
  onSubmit,
  id = "cost-search",
  label = "Find calls",
  placeholder = "Lesson number, learner email or job id",
  check = checkCallSearch,
}: {
  value: string;
  onSubmit: (value: string | undefined) => void;
  id?: string;
  label?: string;
  placeholder?: string;
  /** What's wrong with a value, or null when it can be searched for. */
  check?: (value: string) => string | null;
}) {
  const [draft, setDraft] = useState(value);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [lastValue, setLastValue] = useState(value);
  // Follow the URL when the search is cleared or set elsewhere.
  if (value !== lastValue) {
    setLastValue(value);
    setDraft(value);
    setInvalid(null);
  }
  const submit = () => {
    const v = draft.trim();
    const problem = v ? check(v) : null;
    if (problem) {
      setInvalid(problem);
      return;
    }
    setInvalid(null);
    onSubmit(v || undefined);
  };
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-xs font-semibold text-slate-600">
        {label}
      </label>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="flex gap-2"
      >
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            id={id}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            aria-invalid={!!invalid}
            aria-describedby={invalid ? `${id}-help` : undefined}
            className={`${fieldClass} pl-9 pr-9 ${invalid ? "border-red-400 focus:border-red-500 focus:ring-red-500/20" : ""}`}
          />
          {draft ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setDraft("");
                setInvalid(null);
                if (value) onSubmit(undefined);
              }}
              className={`absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-600 ${focusRing}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
        <button type="submit" className={`h-10 shrink-0 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 ${focusRing}`}>
          Search
        </button>
      </form>
      {invalid ? (
        <p id={`${id}-help`} className="mt-1 text-xs text-red-600">
          {invalid}
        </p>
      ) : null}
    </div>
  );
}

function selectAllSoon(input: HTMLInputElement) {
  setTimeout(() => input.select(), 0);
}

/**
 * A searchable filter dropdown, for long lists such as learners: type to narrow the options, pick
 * one, and clear it with the × button. The empty value means "All". Same look as FilterListbox.
 * The selection is null when nothing is picked (not ""), which is how the Combobox models "none".
 */
export function FilterCombobox({
  label,
  placeholder,
  value,
  onChange,
  options,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  options: { key: string; label: string }[];
}) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const shown = (q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options).slice(0, 100);
  const labelFor = (key: string | null) => (key ? (options.find((o) => o.key === key)?.label ?? key) : "");
  return (
    <div className="min-w-0">
      <Combobox
        value={value || null}
        onChange={(next: string | null) => {
          if (next) onChange(next);
        }}
        onClose={() => setQuery("")}
        immediate
      >
        <span className="mb-1 block text-xs font-semibold text-slate-600">{label}</span>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <ComboboxInput
            aria-label={label}
            className="block h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-16 text-sm font-medium text-slate-900 shadow-sm transition placeholder:font-normal placeholder:text-slate-500 hover:border-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            displayValue={labelFor}
            placeholder={placeholder}
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
            // Select the whole entry on focus and on every click, so typing replaces the chosen
            // learner instead of landing in the middle of their email. Deferred a tick: the
            // browser places the caret after the click handlers run, which would undo it.
            onFocus={(e) => selectAllSoon(e.currentTarget)}
            onClick={(e) => selectAllSoon(e.currentTarget)}
          />
          {value ? (
            <button
              type="button"
              aria-label={`Clear ${label.toLowerCase()}`}
              onClick={() => onChange("")}
              className={`absolute right-8 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-600 ${focusRing}`}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
          <ComboboxButton aria-label={`Show all ${label.toLowerCase()} options`} className="absolute inset-y-0 right-0 flex items-center rounded-r-lg px-3 text-slate-400 hover:text-slate-600">
            <ChevronsUpDown className="h-4 w-4" aria-hidden="true" />
          </ComboboxButton>
        </div>
        <ComboboxOptions
          anchor="bottom start"
          transition
          className="z-50 w-[var(--input-width)] min-w-[16rem] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-lg shadow-slate-900/10 [--anchor-gap:6px] [--anchor-max-height:20rem] empty:invisible focus:outline-none transition duration-100 ease-out data-[closed]:-translate-y-1 data-[closed]:opacity-0"
        >
          {shown.map((o) => (
            <ComboboxOption
              key={o.key}
              value={o.key}
              className="group flex cursor-pointer select-none items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-800 data-[focus]:bg-indigo-50"
            >
              <Check className="h-4 w-4 shrink-0 text-indigo-600 invisible group-data-[selected]:visible" aria-hidden="true" />
              <span className="min-w-0 truncate font-medium group-data-[selected]:text-indigo-700">{o.label}</span>
            </ComboboxOption>
          ))}
          {q && shown.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-slate-500">No learner in this list matches &ldquo;{query.trim()}&rdquo;. It holds the 500 highest spenders; the Learners tab can find anyone.</p>
          ) : null}
        </ComboboxOptions>
      </Combobox>
    </div>
  );
}
